import React, { useState } from 'react';
import { 
  ChevronDown, 
  Linkedin, 
  Mail, 
  MapPin, 
  Phone,
  Server,
  Cloud,
  Cpu,
  Network,
  Shield,
  Wrench,
  Users,
  TrendingUp,
  CheckCircle,
  ExternalLink,
  Menu,
  X,
  ArrowRight,
  Zap,
  Target,
  Award
} from 'lucide-react';

// Wolf Logo Component
const WolfLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_kotf-eco-dev/artifacts/5bf363m2_T%20Logo.png" 
    alt="Tyron Logo" 
    className="h-12 w-auto"
  />
);

// Navigation Component
const TyronNav = () => {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Experience', href: '#experience' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-3">
            <WolfLogo />
            <span className="text-white font-bold text-xl">TYRON</span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-gray-300 hover:text-blue-400 transition-colors text-sm font-medium"
              >
                {link.name}
              </a>
            ))}
            <a
              href="https://www.linkedin.com/in/cherise-t/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
            >
              <Linkedin size={16} />
              <span>Connect</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-gray-300 hover:text-white"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-gray-800">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="block py-2 text-gray-300 hover:text-blue-400 transition-colors"
              >
                {link.name}
              </a>
            ))}
            <a
              href="https://www.linkedin.com/in/cherise-t/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
            >
              <Linkedin size={16} />
              <span>Connect</span>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const TyronHero = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        {/* Wolf silhouette background effect */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]">
            <img 
              src="https://customer-assets.emergentagent.com/job_kotf-eco-dev/artifacts/5bf363m2_T%20Logo.png" 
              alt="" 
              className="w-full h-full object-contain opacity-20"
            />
          </div>
        </div>
        {/* Grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        {/* Gradient orbs */}
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gray-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_kotf-eco-dev/artifacts/5bf363m2_T%20Logo.png" 
            alt="Tyron Logo" 
            className="h-32 w-auto mx-auto mb-6 drop-shadow-2xl"
          />
        </div>

        <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">
          TYRON
        </h1>

        <div className="flex items-center justify-center space-x-3 mb-6">
          <span className="text-blue-400 font-semibold">Innovation</span>
          <span className="text-gray-500">/</span>
          <span className="text-gray-300 font-semibold">Recovery</span>
          <span className="text-gray-500">/</span>
          <span className="text-blue-400 font-semibold">Transformation</span>
        </div>

        <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-10">
          Bridging Physical Systems to Cloud & Automation
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://www.linkedin.com/in/cherise-t/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center space-x-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
            data-testid="hero-connect-btn"
          >
            <Linkedin size={20} />
            <span>Connect</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#about"
            className="flex items-center space-x-2 px-8 py-4 border border-gray-600 hover:border-blue-500 text-gray-300 hover:text-blue-400 rounded-xl font-semibold transition-all"
          >
            <span>Learn More</span>
            <ChevronDown size={18} />
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="text-gray-500" size={32} />
      </div>
    </section>
  );
};

// About Section
const TyronAbout = () => {
  return (
    <section id="about" className="py-24 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">About Me</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-700 mx-auto"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image/Visual */}
          <div className="relative">
            <div className="aspect-square rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 overflow-hidden flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_kotf-eco-dev/artifacts/5bf363m2_T%20Logo.png" 
                alt="Tyron" 
                className="w-3/4 h-3/4 object-contain opacity-80"
              />
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-blue-500/30 rounded-2xl"></div>
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-blue-500/10 rounded-2xl"></div>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-2xl font-bold text-blue-400 mb-4">Field Technician & Infrastructure Specialist</h3>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Field Technician with extensive experience across telecom infrastructure and property technology, 
              specializing in submetering systems for multi-family properties. Skilled in installation, 
              troubleshooting, system recovery, and rescuing failed or stalled submetering projects.
            </p>
            <p className="text-gray-400 leading-relaxed mb-8">
              Known for identifying root-cause issues and eliminating preventable delays through proactive 
              coordination and technical accuracy. Currently advancing skills in network operations, 
              cloud platforms, and system automation to transition into higher-level infrastructure and AWS/cloud roles.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <div className="text-3xl font-bold text-blue-400">10+</div>
                <div className="text-gray-400 text-sm">Years Experience</div>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <div className="text-3xl font-bold text-blue-400">90%</div>
                <div className="text-gray-400 text-sm">Delay Prevention</div>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <div className="text-3xl font-bold text-blue-400">300+</div>
                <div className="text-gray-400 text-sm">Units Deployed</div>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <div className="text-3xl font-bold text-blue-400">8K+</div>
                <div className="text-gray-400 text-sm">Users Supported</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Storyteller Section
const TyronStoryteller = () => {
  return (
    <section className="py-24 bg-gray-900 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-8">
          <Target className="w-16 h-16 text-blue-500 mx-auto mb-6" />
        </div>

        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
          The Journey of Transformation
        </h2>

        <blockquote className="text-xl md:text-2xl text-gray-300 italic leading-relaxed mb-8">
          "From hands-on field work installing physical systems, to orchestrating cloud infrastructure — 
          my journey has always been about one thing: <span className="text-blue-400 font-semibold">solving problems 
          others walk away from</span>. Every failed deployment I've rescued, every delayed project I've 
          accelerated, has built the foundation for the next level."
        </blockquote>

        <div className="flex items-center justify-center space-x-4 text-gray-500">
          <div className="w-12 h-[2px] bg-gray-700"></div>
          <span className="font-semibold text-blue-400">TYRON</span>
          <div className="w-12 h-[2px] bg-gray-700"></div>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <Zap className="w-10 h-10 text-blue-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Innovation</h3>
            <p className="text-gray-400 text-sm">Finding new solutions to old problems through technical creativity</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <Wrench className="w-10 h-10 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Recovery</h3>
            <p className="text-gray-400 text-sm">Rescuing failing deployments and turning setbacks into success</p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <TrendingUp className="w-10 h-10 text-blue-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Transformation</h3>
            <p className="text-gray-400 text-sm">Evolving from physical systems to cloud & automation mastery</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Skills Section
const TyronSkills = () => {
  const skillCategories = [
    {
      title: 'Infrastructure & Hardware',
      icon: Server,
      color: 'blue',
      skills: [
        'Submetering Systems Installation',
        'Data Center Operations',
        'Hardware Installation & Replacement',
        'Server Rack Deployment',
        'Copper/Coax/Cat5 Cabling',
        'Cross-connects & Circuit Validation'
      ]
    },
    {
      title: 'Network & Telecom',
      icon: Network,
      color: 'gray',
      skills: [
        'Telecom Infrastructure',
        'Network Troubleshooting',
        'Cisco Unified Communications',
        'IP Phone Fleet Management',
        'Call Manager Administration',
        'Voice/Video Endpoints (CUCM, TMS)'
      ]
    },
    {
      title: 'Cloud & Automation',
      icon: Cloud,
      color: 'blue',
      skills: [
        'AWS/Cloud Platforms',
        'System Automation',
        'Webex Cloud Services',
        'Hybrid UC Services',
        'Cloud UC Administration',
        'Remote Infrastructure Support'
      ]
    },
    {
      title: 'Project & Operations',
      icon: Users,
      color: 'gray',
      skills: [
        'Project Recovery & Rescue',
        'Root Cause Analysis',
        'Cross-functional Communication',
        'Site Planning & Coordination',
        'VIP/Executive Support',
        'Global Team Collaboration'
      ]
    }
  ];

  return (
    <section id="skills" className="py-24 bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Skills & Expertise</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-700 mx-auto mb-6"></div>
          <p className="text-gray-400 max-w-2xl mx-auto">
            A comprehensive toolkit built through years of hands-on experience across multiple technology domains
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {skillCategories.map((category, idx) => (
            <div 
              key={idx} 
              className="bg-gray-800/30 rounded-2xl p-8 border border-gray-700 hover:border-blue-500/50 transition-all"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  category.color === 'blue' ? 'bg-blue-500/20' : 'bg-gray-600/20'
                }`}>
                  <category.icon className={`${
                    category.color === 'blue' ? 'text-blue-400' : 'text-gray-400'
                  }`} size={24} />
                </div>
                <h3 className="text-xl font-bold text-white">{category.title}</h3>
              </div>
              
              <div className="space-y-3">
                {category.skills.map((skill, skillIdx) => (
                  <div key={skillIdx} className="flex items-center space-x-3">
                    <CheckCircle className={`${
                      category.color === 'blue' ? 'text-blue-400' : 'text-gray-500'
                    }`} size={16} />
                    <span className="text-gray-300">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Experience Timeline Section
const TyronExperience = () => {
  const experiences = [
    {
      title: 'Field Technician III',
      company: 'Submetering & Property Technology',
      period: 'Current',
      color: 'blue',
      highlights: [
        'Identified coordination gaps causing recurring 3-month submetering delays; improved project readiness across 10+ general contractors',
        'Installed, tested, and validated submetering systems for properties ranging 50–300 units',
        'Conducted troubleshooting and rescue of failing deployments, reducing rework and accelerating unit readiness',
        'Provided cross-functional communication that prevented up to 90% of typical delays through proactive site planning'
      ]
    },
    {
      title: 'CSC Technician II',
      company: 'Data Center Operations',
      period: 'Previous',
      color: 'gray',
      highlights: [
        'Provided weekend remote-hands support for data center clients, including hardware swaps, testing, and escalations',
        'Performed back-up tape rotations, media handling, and data storage maintenance for enterprise environments',
        'Executed copper/coax/cat5 cabling, cross-connects, and circuit validation for new deployments',
        'Installed and replaced servers, network hardware, and software packages in production racks'
      ]
    },
    {
      title: 'Ace Collaboration Analyst',
      company: 'Cisco Systems',
      period: 'Previous',
      color: 'blue',
      highlights: [
        'Delivered advanced technical and collaboration support for 8,000+ global Cisco Sales users',
        'Managed Webex meetings, scheduling, and conferencing; supported hybrid and cloud UC services',
        'Administered voice/video endpoints (CUCM, TMS, Webex Cloud) and participated in early-field testing for new collaboration devices',
        'Provided VIP/Executive-level support for Directors, VPs, and Executive teams'
      ]
    },
    {
      title: 'Unified Collaboration Engineer',
      company: 'Cisco Unified Communications',
      period: 'Previous',
      color: 'gray',
      highlights: [
        'Tier II engineer for Cisco Unified Communications; supported global IP phone fleets across numerous device models',
        'Administered Cisco Call Manager and Unified Communications systems; troubleshot IPT, UCCE, and Contact Center environments',
        'Supported CTIOS, WebACD, CUIC reporting, Iceberg applications, and call-routing logic',
        'Partnered with global teams to improve reliability and reduce ticket escalations'
      ]
    }
  ];

  return (
    <section id="experience" className="py-24 bg-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Experience</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-700 mx-auto mb-6"></div>
          <p className="text-gray-400">A track record of technical excellence and problem-solving</p>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-[2px] bg-gray-800 transform md:-translate-x-1/2"></div>

          {experiences.map((exp, idx) => (
            <div key={idx} className={`relative flex flex-col md:flex-row gap-8 mb-12 ${
              idx % 2 === 0 ? 'md:flex-row-reverse' : ''
            }`}>
              {/* Timeline dot */}
              <div className={`absolute left-0 md:left-1/2 w-4 h-4 rounded-full transform -translate-x-1/2 ${
                exp.color === 'blue' ? 'bg-blue-500' : 'bg-gray-600'
              }`}></div>

              {/* Content */}
              <div className={`ml-8 md:ml-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                <div className={`bg-gray-800/50 rounded-2xl p-6 border ${
                  exp.color === 'blue' ? 'border-blue-500/30' : 'border-gray-700'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white">{exp.title}</h3>
                      <p className={`${exp.color === 'blue' ? 'text-blue-400' : 'text-gray-400'}`}>
                        {exp.company}
                      </p>
                    </div>
                    <span className="text-sm text-gray-500 bg-gray-700/50 px-3 py-1 rounded-full">
                      {exp.period}
                    </span>
                  </div>
                  
                  <ul className="space-y-3">
                    {exp.highlights.map((highlight, hIdx) => (
                      <li key={hIdx} className="flex items-start space-x-3">
                        <CheckCircle className={`mt-1 flex-shrink-0 ${
                          exp.color === 'blue' ? 'text-blue-400' : 'text-gray-500'
                        }`} size={14} />
                        <span className="text-gray-300 text-sm">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section (Visual Only)
const TyronContact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your interest! Contact form submission is coming soon. Please connect via LinkedIn for now.');
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Get In Touch</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-700 mx-auto mb-6"></div>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Ready to discuss your infrastructure needs? Let's connect.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-gray-800/30 rounded-2xl p-8 border border-gray-700">
              <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Mail className="text-blue-400" size={20} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <p className="text-white">tyron@spirit.com</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Linkedin className="text-blue-400" size={20} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">LinkedIn</p>
                    <a 
                      href="https://www.linkedin.com/in/cherise-t/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                    >
                      <span>Connect on LinkedIn</span>
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-600/20 rounded-xl flex items-center justify-center">
                    <MapPin className="text-gray-400" size={20} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Location</p>
                    <p className="text-white">United States</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500/10 to-gray-800/30 rounded-2xl p-8 border border-blue-500/20">
              <Award className="w-12 h-12 text-blue-400 mb-4" />
              <h4 className="text-xl font-bold text-white mb-2">Let's Build Something Great</h4>
              <p className="text-gray-400">
                Whether you need infrastructure support, system recovery, or cloud migration guidance — 
                I'm here to help transform your technical challenges into solutions.
              </p>
            </div>
          </div>

          {/* Contact Form (Visual) */}
          <div className="bg-gray-800/30 rounded-2xl p-8 border border-gray-700">
            <h3 className="text-2xl font-bold text-white mb-6">Send a Message</h3>
            <p className="text-gray-400 text-sm mb-6">* Form submission coming soon. Please use LinkedIn to connect.</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows="5"
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Your message..."
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-lg font-semibold transition-all shadow-lg shadow-blue-500/25"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const TyronFooter = () => {
  return (
    <footer className="bg-black border-t border-gray-800 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center space-x-3">
            <WolfLogo />
            <div>
              <span className="text-white font-bold text-xl">TYRON</span>
              <p className="text-gray-500 text-sm">Innovation / Recovery / Transformation</p>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <a href="#home" className="text-gray-400 hover:text-white transition-colors text-sm">Home</a>
            <a href="#about" className="text-gray-400 hover:text-white transition-colors text-sm">About</a>
            <a href="#skills" className="text-gray-400 hover:text-white transition-colors text-sm">Skills</a>
            <a href="#experience" className="text-gray-400 hover:text-white transition-colors text-sm">Experience</a>
            <a href="#contact" className="text-gray-400 hover:text-white transition-colors text-sm">Contact</a>
          </div>

          <a
            href="https://www.linkedin.com/in/cherise-t/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Linkedin size={20} />
            <span>Connect</span>
          </a>
        </div>

        {/* Legal Links */}
        <div className="mt-8 pt-6 border-t border-gray-800">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/tyron/privacy" className="text-gray-500 hover:text-gray-300 transition-colors">Privacy Policy</a>
            <a href="/tyron/terms" className="text-gray-500 hover:text-gray-300 transition-colors">Terms of Service</a>
            <a href="/tyron/disclaimer" className="text-gray-500 hover:text-gray-300 transition-colors">Disclaimer</a>
            <a href="/tyron/ai-policy" className="text-gray-500 hover:text-gray-300 transition-colors">AI Usage Policy</a>
            <a href="/tyron/dpa" className="text-gray-500 hover:text-gray-300 transition-colors">Data Processing</a>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Tyron. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Portfolio Component
const TyronPortfolio = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <TyronNav />
      <TyronHero />
      <TyronAbout />
      <TyronStoryteller />
      <TyronSkills />
      <TyronExperience />
      <TyronContact />
      <TyronFooter />
    </div>
  );
};

export default TyronPortfolio;
